import {
  Play,
  CheckCircle,
  AlertCircle,
  Users,
  Building2,
  TrendingUp,
  XCircle,
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { SkippedRecordsTable } from './SkippedRecordsTable';

import type {
  ImportValidationResult,
  FundMatchingData,
  ExistingInvestorConflict,
  ImportResult,
} from '../../types/investor-import';

interface ImportExecutionStepProps {
  sessionId: string;
  validationResult: ImportValidationResult;
  fundMatches: FundMatchingData[];
  conflicts: ExistingInvestorConflict[];
  importResult: ImportResult | null;
  onImportCompleted: (result: ImportResult) => void;
}

export function ImportExecutionStep({
  sessionId: _sessionId,
  validationResult,
  fundMatches,
  conflicts,
  importResult,
  onImportCompleted: _onImportCompleted,
}: ImportExecutionStepProps) {
  // Calculate statistics
  const matchedFunds = fundMatches.filter((f) => f.is_matched).length;
  const unmatchedFunds = fundMatches.filter((f) => !f.is_matched).length;
  const conflictsToOverwrite = conflicts.filter(
    (c) => c.should_overwrite
  ).length;
  const conflictsToSkip = conflicts.filter((c) => !c.should_overwrite).length;

  // Calculate investors that will actually be imported based on mapped funds
  const mappedFundNames = fundMatches
    .filter((fm) => fm.is_matched)
    .map((fm) => fm.import_fund_name);

  const investorsForMappedFunds = validationResult.investors_preview.filter(
    (investor) =>
      investor.fund_names.some((fundName) => mappedFundNames.includes(fundName))
  ).length;

  const expectedInvestors = investorsForMappedFunds - conflictsToSkip;
  const expectedContacts = validationResult.total_contacts; // TODO: This should also be filtered by mapped funds

  if (importResult) {
    return (
      <div className="space-y-6">
        {/* Import Results */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {importResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
              Import{' '}
              {importResult.success ? 'Completed Successfully' : 'Failed'}
            </CardTitle>
            <CardDescription>
              {importResult.success
                ? 'Your investor data has been imported successfully.'
                : 'The import process encountered errors.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Success Summary */}
            {importResult.success && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-900">
                      Investors Created
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-green-900">
                    {importResult.investors_created}
                  </div>
                </div>

                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">
                      Investors Updated
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-blue-900">
                    {importResult.investors_updated}
                  </div>
                </div>

                <div className="bg-purple-50 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-900">
                      Contacts Created
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-purple-900">
                    {importResult.contacts_created}
                  </div>
                </div>

                <div className="bg-orange-50 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-900">
                      Fund Relationships
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-orange-900">
                    {importResult.fund_relationships_created}
                  </div>
                </div>
              </div>
            )}

            {/* Warnings */}
            {importResult.warnings.length > 0 && (
              <Alert className="border-yellow-200 bg-yellow-50">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription>
                  <div className="font-medium text-yellow-800 mb-2">
                    Import Warnings ({importResult.warnings.length}):
                  </div>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    {importResult.warnings.slice(0, 5).map((warning) => (
                      <li key={warning}>• {warning}</li>
                    ))}
                    {importResult.warnings.length > 5 && (
                      <li>
                        ... and {importResult.warnings.length - 5} more warnings
                      </li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Errors */}
            {importResult.errors.length > 0 && (
              <Alert className="border-red-200 bg-red-50">
                <XCircle className="h-4 w-4 text-red-600" />
                <AlertDescription>
                  <div className="font-medium text-red-800 mb-2">
                    Import Errors ({importResult.errors.length}):
                  </div>
                  <ul className="text-sm text-red-700 space-y-1">
                    {importResult.errors.slice(0, 5).map((error) => (
                      <li key={error}>• {error}</li>
                    ))}
                    {importResult.errors.length > 5 && (
                      <li>
                        ... and {importResult.errors.length - 5} more errors
                      </li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Success Message */}
            {importResult.success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">
                      Import Completed Successfully!
                    </p>
                    <p>
                      {importResult.investors_created +
                        importResult.investors_updated}{' '}
                      investors and {importResult.contacts_created} contacts
                      have been processed.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Skipped Records Table */}
        {importResult.skipped_records.length > 0 && (
          <SkippedRecordsTable skippedRecords={importResult.skipped_records} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Import Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="w-5 h-5 text-blue-600" />
            Ready to Execute Import
          </CardTitle>
          <CardDescription>
            Review the import summary below and execute the import when ready.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-sm font-medium text-blue-900">
                Expected Investors
              </div>
              <div className="text-2xl font-bold text-blue-900">
                {expectedInvestors}
              </div>
              <div className="text-xs text-blue-700">
                {conflictsToSkip > 0 && `(${conflictsToSkip} skipped)`}
              </div>
            </div>

            <div className="bg-green-50 p-3 rounded-lg">
              <div className="text-sm font-medium text-green-900">
                Expected Contacts
              </div>
              <div className="text-2xl font-bold text-green-900">
                {expectedContacts}
              </div>
            </div>

            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="text-sm font-medium text-purple-900">
                Funds Matched
              </div>
              <div className="text-2xl font-bold text-purple-900">
                {matchedFunds}
              </div>
              <div className="text-xs text-purple-700">
                of {fundMatches.length} total
              </div>
            </div>

            <div className="bg-orange-50 p-3 rounded-lg">
              <div className="text-sm font-medium text-orange-900">Updates</div>
              <div className="text-2xl font-bold text-orange-900">
                {conflictsToOverwrite}
              </div>
              <div className="text-xs text-orange-700">overwrite existing</div>
            </div>
          </div>

          {/* Warning for unmapped funds */}
          {unmatchedFunds > 0 && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Note:</strong> {unmatchedFunds} fund
                {unmatchedFunds !== 1 ? 's' : ''} will be skipped. Investors
                associated with these funds will not be imported.
              </AlertDescription>
            </Alert>
          )}

          {/* Configuration Summary */}
          <div className="space-y-3">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">
                Import Configuration:
              </h4>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span>Source file:</span>
                  <span className="font-medium">
                    {validationResult.total_investors} investors,{' '}
                    {validationResult.total_contacts} contacts
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Fund matching:</span>
                  <span className="font-medium">
                    {matchedFunds} matched
                    {unmatchedFunds > 0 && `, ${unmatchedFunds} skipped`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Conflict resolution:</span>
                  <span className="font-medium">
                    {conflictsToOverwrite} updates, {conflictsToSkip} skips
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Create new funds:</span>
                  <span className="font-medium text-red-600">
                    No (disabled)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Final Checks */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-medium mb-1">
                Before executing the import:
              </div>
              <ul className="text-sm space-y-1">
                <li>• Ensure all fund mappings are correct</li>
                <li>• Verify conflict resolution choices</li>
                <li>• Consider creating a database backup</li>
                <li>• Import cannot be undone automatically</li>
              </ul>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Pre-import Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Pre-import Checklist</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-gray-600">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>File validation passed</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>All funds properly matched</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span>Conflicts reviewed and resolved</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-600" />
              <span>No automatic rollback - ensure database backup exists</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
